import { type FC, useState } from 'react';
import { Show } from '@components/Show';
import { cn } from 'cn';
import { ChevronRight } from 'lucide-react';

import { checkVerdictClasses, checkVerdictGlyph, sectionHeaderClasses } from '../classes';
import { CHECK_VERDICT_HINT, CHECK_VERDICT_LABEL } from '../constants';
import type { EvalCheckResult } from '../types';
import type { ChecklistRegister } from '../utils/checklistRegisters';
import { VERDICT_GROUP_ORDER } from '../utils/checklistRegisters';

interface ChecklistSectionProps {
  registers: ChecklistRegister[];
  /** check_id → finding id, so a failed check can jump to its finding card. */
  findingIdByCheckId: Map<string, string>;
  onViewFinding: (findingId: string) => void;
}

/**
 * Audit coverage, kept deliberately quiet.
 *
 * A failed check already produces a finding card below, so this section never
 * repeats that content — its one job is the question findings can't answer:
 * "was everything on the checklist actually assessed, and what passed?"
 *
 * Collapsed (the default) that's a single row per checklist: name + a colored
 * verdict tally. Expanded, it's one line per check — glyph, text, tooltip —
 * and clicking a failed check's row jumps to its finding. All detail beyond
 * one line lives in tooltips; the drawer is 340px and the findings below are
 * the main event.
 */
export const ChecklistSection: FC<ChecklistSectionProps> = ({ registers, findingIdByCheckId, onViewFinding }) => {
  if (registers.length === 0) return null;

  return (
    <div className="border-t border-gray-200 bg-white pb-2">
      <h3 className={sectionHeaderClasses}>Checklist</h3>
      {registers.map((register) => (
        <ChecklistRegisterBlock
          key={register.packKey}
          register={register}
          findingIdByCheckId={findingIdByCheckId}
          onViewFinding={onViewFinding}
        />
      ))}
    </div>
  );
};

const ChecklistRegisterBlock: FC<{
  register: ChecklistRegister;
  findingIdByCheckId: Map<string, string>;
  onViewFinding: (findingId: string) => void;
}> = ({ register, findingIdByCheckId, onViewFinding }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasProblems = register.counts.FAIL + register.counts.NOT_EVIDENCED > 0 || !register.isComplete;

  return (
    <div data-testid={`pbqa-checklist-${register.packKey}`}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        data-testid={`pbqa-checklist-toggle-${register.packKey}`}
        className="flex w-full items-center gap-1.5 px-4 py-1.5 text-left hover:bg-gray-50"
      >
        <ChevronRight className={cn('size-3 shrink-0 text-gray-400 transition-transform', isOpen && 'rotate-90')} />
        <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{register.packName}</span>
        {/* Verdict tally, colored glyph + count, problems first. */}
        <span
          className="flex shrink-0 items-center gap-2 text-[11px] font-medium tabular-nums"
          data-testid={`pbqa-checklist-tally-${register.packKey}`}
        >
          {VERDICT_GROUP_ORDER.filter((verdict) => register.counts[verdict] > 0).map((verdict) => (
            <span
              key={verdict}
              className={checkVerdictClasses[verdict]}
              title={`${register.counts[verdict]} ${CHECK_VERDICT_LABEL[verdict].toLowerCase()} — ${CHECK_VERDICT_HINT[verdict]}`}
            >
              {checkVerdictGlyph[verdict]} {register.counts[verdict]}
            </span>
          ))}
        </span>
      </button>

      <Show when={register.isInProgress && register.isComplete}>
        <p
          className="px-4 pb-1 pl-[34px] text-[11px] text-gray-400"
          data-testid={`pbqa-checklist-progress-${register.packKey}`}
        >
          Audit in progress…
        </p>
      </Show>

      {/* Coverage break — the one state that must interrupt, since silence
          here would read as "everything was checked" when it wasn't. */}
      <Show when={!register.isComplete}>
        <div
          className="text-error-700 bg-error-50 mx-4 mb-1 rounded-md px-2 py-1 text-[11px]"
          data-testid={`pbqa-checklist-warning-${register.packKey}`}
        >
          {register.warnings[0] ?? 'Not every check in this checklist was assessed.'}
        </div>
      </Show>

      <Show when={isOpen}>
        <ul className="mb-1">
          {register.results.map((result) => (
            <ChecklistRow
              key={result.check_id}
              result={result}
              findingId={findingIdByCheckId.get(result.check_id)}
              onViewFinding={onViewFinding}
            />
          ))}
        </ul>
      </Show>
      <Show when={!isOpen && hasProblems && register.counts.FAIL + register.counts.NOT_EVIDENCED > 0}>
        <p className="px-4 pb-1 pl-[34px] text-[11px] text-gray-500">Failed checks appear as findings below.</p>
      </Show>
    </div>
  );
};

/**
 * One check, one line: verdict glyph + check text. The full text, the id and
 * the model's note all live in the tooltip. A check with a linked finding
 * makes the whole row a jump target instead of growing a separate link.
 */
const ChecklistRow: FC<{
  result: EvalCheckResult;
  findingId?: string;
  onViewFinding: (findingId: string) => void;
}> = ({ result, findingId, onViewFinding }) => {
  const text = result.check_text ?? result.check_id;
  const tooltip = [
    `${result.check_id} — ${CHECK_VERDICT_LABEL[result.verdict]}`,
    result.check_text,
    result.note,
    findingId ? 'Click to view the finding.' : undefined,
  ]
    .filter(Boolean)
    .join('\n');

  const content = (
    <>
      <span className={cn('w-3 shrink-0 text-center font-medium', checkVerdictClasses[result.verdict])} aria-hidden>
        {checkVerdictGlyph[result.verdict]}
      </span>
      <span className={cn('min-w-0 flex-1 truncate', result.verdict === 'NOT_APPLICABLE' && 'text-gray-400')}>
        {text}
      </span>
    </>
  );

  const rowClasses = 'flex w-full items-center gap-1.5 py-[3px] pr-4 pl-[34px] text-left text-[11px] text-gray-600';

  return (
    <li data-testid={`pbqa-check-${result.check_id}`} title={tooltip}>
      {findingId ? (
        <button
          type="button"
          onClick={() => onViewFinding(findingId)}
          data-testid={`pbqa-check-view-finding-${result.check_id}`}
          className={cn(rowClasses, 'hover:bg-gray-50 hover:text-gray-800')}
        >
          {content}
        </button>
      ) : (
        <div className={rowClasses}>{content}</div>
      )}
    </li>
  );
};
